// Ported from dev-scrips-pm-react src/billing/pages/invoice-detail-page.tsx (PROD-3312 appointment Billing tab): the
// "Issue this invoice?" AlertDialog (Flutter `InvoiceDateSelectionPopUp`) and its handler.
import { useState } from 'react';
import { TextField } from '@scripsteam/scrips-signal-ds';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/billing/bridge';
import { useIssueInvoice } from '@/billing/services/use-invoice';
import { serverErrorMessage } from '@/shared/lib/toast-helpers';

interface InvoiceIssueDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  invoiceId: string;
  /** The picked `YYYY-MM-DD`; the page seeds it with the encounter date when Issue is clicked. */
  issueDate: string;
  onIssueDateChange: (issueDate: string) => void;
  /** Lower bound of the picker: the encounter date part. */
  minDate: string;
  /** Upper bound of the picker: today (`YYYY-MM-DD`). */
  maxDate: string;
  /** The encounter's time of day, no Z, padded to `.000` (`00:00:00.000` when it has none). */
  encTimePart: string;
  /** The Draft note the user typed; `null` = untouched. */
  noteDraft: string | null;
  /** The stored `invoice.note`. */
  storedNote: string | null | undefined;
}

export function InvoiceIssueDialog({
  open,
  onOpenChange,
  invoiceId,
  issueDate,
  onIssueDateChange,
  minDate,
  maxDate,
  encTimePart,
  noteDraft,
  storedNote,
}: InvoiceIssueDialogProps) {
  const issueMutation = useIssueInvoice();
  // The server's reason for a refused issue (pm-react PROD-1760). It remembers the note it was raised under, so editing
  // the note on the page (which this dialog does not own) hides it without the page having to tell us.
  const [issueError, setIssueError] = useState<{ message: string; note: string | null } | null>(null);
  const visibleError = issueError && issueError.note === noteDraft ? issueError.message : null;

  const handleOpenChange = (next: boolean) => {
    setIssueError(null);
    onOpenChange(next);
  };

  const onConfirmIssue = async () => {
    if (!invoiceId || !issueDate) return;
    setIssueError(null);
    try {
      await issueMutation.mutateAsync({
        invoiceId,
        // No-Z ISO (billing-surface convention): the picked date at the encounter's time of day.
        invoiceDate: `${issueDate}T${encTimePart}`,
        // PROD-1760. `??` (not `||`) on purpose: clearing the field yields `''`, which must be sent as an intentional
        // erase rather than falling through to the old value. An untouched field re-sends the stored note instead of
        // `null`, so a backend that treats `null` as "overwrite" cannot lose it (see `IssueInvoiceVars.note`).
        note: noteDraft ?? storedNote ?? null,
      });
      onOpenChange(false);
    } catch (err) {
      // The mutation is `handledInline`, so no toast fires; the reason shows in the dialog, which stays open for retry.
      setIssueError({ message: serverErrorMessage(err), note: noteDraft });
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Issue this invoice?</AlertDialogTitle>
          <AlertDialogDescription>
            Issuing finalises the invoice and assigns an invoice number. Confirm the invoice date.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="space-y-2 px-1">
          <TextField
            label="Invoice Date"
            type="date"
            size="compact"
            value={issueDate}
            min={minDate}
            max={maxDate}
            onChange={(e) => {
              setIssueError(null);
              onIssueDateChange(e.target.value);
            }}
          />
          {visibleError && (
            <div
              role="alert"
              className="rounded-md border border-status-error bg-surface-card px-4 py-3 text-sm text-status-error whitespace-pre-line"
            >
              {visibleError}
            </div>
          )}
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={issueMutation.isPending}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirmIssue}
            disabled={!issueDate || issueMutation.isPending}
            className="bg-brand-depth hover:bg-brand-primary text-white"
          >
            {issueMutation.isPending ? 'Issuing…' : 'Issue invoice'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
